import { translateUi } from "@i18n/config";
export const botProtectionActions = {
  adminLogin: "admin_login",
  adminRegister: "admin_register",
  adminPasswordResetRequest: "admin_password_reset_request",
  storeSignup: "store_signup",
} as const;

type RecaptchaEnterpriseApi = {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: { enterprise?: RecaptchaEnterpriseApi };
  }
}

let loader: Promise<RecaptchaEnterpriseApi> | null = null;

function siteKey(): string {
  return String(import.meta.env.VITE_RECAPTCHA_SITE_KEY || "").trim();
}

function loadRecaptcha(key: string): Promise<RecaptchaEnterpriseApi> {
  const existing = window.grecaptcha?.enterprise;
  if (existing) return Promise.resolve(existing);
  if (loader) return loader;

  loader = new Promise<RecaptchaEnterpriseApi>((resolve, reject) => {
    const scriptId = "storizone-recaptcha-enterprise";
    let script = document.querySelector<HTMLScriptElement>(`#${scriptId}`);
    const timeout = window.setTimeout(() => {
      loader = null;
      reject(
        new Error(
          translateUi("humanVerificationTimedOut", "Human verification timed out."),
        ),
      );
    }, 10_000);

    const finish = () => {
      const api = window.grecaptcha?.enterprise;
      if (!api) {
        window.clearTimeout(timeout);
        loader = null;
        reject(
          new Error(
            translateUi(
              "humanVerificationDidNotInitialize",
              "Human verification did not initialize.",
            ),
          ),
        );
        return;
      }
      api.ready(() => {
        window.clearTimeout(timeout);
        resolve(api);
      });
    };

    const isNewScript = !script;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.defer = true;
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(key)}`;
    }

    script.addEventListener("load", finish, { once: true });
    script.addEventListener(
      "error",
      () => {
        window.clearTimeout(timeout);
        loader = null;
        reject(
          new Error(
            translateUi(
              "humanVerificationCouldNotBeLoaded",
              "Human verification could not be loaded.",
            ),
          ),
        );
      },
      { once: true },
    );
    if (isNewScript) document.head.append(script);
  });

  return loader;
}

export async function executeBotProtection(
  action: string,
): Promise<string | undefined> {
  const key = siteKey();
  if (!key || typeof window === "undefined") return undefined;
  const api = await loadRecaptcha(key);
  return await api.execute(key, { action });
}

export async function botProtectionHeaders(
  action: string,
): Promise<Record<string, string>> {
  const token = await executeBotProtection(action);
  return token ? { "X-Bot-Token": token } : {};
}
